import React, { useState, useEffect, useCallback } from "react";
import Modal from "./Modal";
import api, { formatApiErrorDetail } from "../lib/api";
import { Analytics } from "../lib/analytics";
import { Gift, CheckCircle2, Tag } from "lucide-react";

const SESSION_KEY = "eduhub_exit_intent_shown";
const VOUCHER_CODE = "LAUNCH10"; // 10% off — must exist in DB

export default function ExitIntentModal() {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");

  const trigger = useCallback(() => {
    if (sessionStorage.getItem(SESSION_KEY)) return;
    setOpen(true);
    sessionStorage.setItem(SESSION_KEY, "1");
  }, []);

  useEffect(() => {
    // Activate only after 10 seconds on the page
    const timer = setTimeout(() => {
      const onMouseLeave = (e) => {
        // Fire when cursor exits through the top of the viewport
        if (e.clientY <= 10) {
          trigger();
          document.removeEventListener("mouseleave", onMouseLeave);
        }
      };
      document.addEventListener("mouseleave", onMouseLeave);
      return () => document.removeEventListener("mouseleave", onMouseLeave);
    }, 10_000);
    return () => clearTimeout(timer);
  }, [trigger]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      await api.post("/leads", {
        email,
        name: "",
        source: "exit_intent",
        message: `Exit-intent lead captured for voucher ${VOUCHER_CODE}`,
        interest: "general",
      });
      Analytics.exitIntentLead();
      setDone(true);
    } catch (er) {
      setErr(formatApiErrorDetail(er.response?.data?.detail) || er.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={() => setOpen(false)}
      eyebrow="Wait — exclusive offer"
      title="Get 10% off your first course"
      maxWidth="max-w-md"
    >
      {done ? (
        <div className="flex flex-col items-center gap-4 py-4 text-center">
          <CheckCircle2 size={40} className="text-eduhu-success" />
          <p className="font-serif text-2xl text-eduhu-dark">Your code is ready!</p>
          <div className="border border-eduhu-gold/40 bg-eduhu-gold/5 p-4 w-full text-center">
            <div className="overline text-eduhu-inkSoft mb-2">Use this code at checkout</div>
            <div className="font-mono text-3xl tracking-[.25em] text-eduhu-dark font-bold flex items-center justify-center gap-2">
              <Tag size={18} className="text-eduhu-gold" /> {VOUCHER_CODE}
            </div>
            <div className="text-xs text-eduhu-inkSoft mt-2">10% off any course · one use per account</div>
          </div>
          <button onClick={() => setOpen(false)} className="eduhu-btn-primary mt-2">Browse courses</button>
        </div>
      ) : (
        <div className="space-y-5">
          <div className="flex items-start gap-3 p-4 bg-eduhu-gold/10 border border-eduhu-gold/30">
            <Gift size={20} className="text-eduhu-gold shrink-0 mt-0.5" />
            <p className="text-sm text-eduhu-dark leading-relaxed">
              Before you go — enter your email and we'll send you a <strong>10% discount code</strong> valid on any EDUHUB course.
            </p>
          </div>
          <form onSubmit={submit} className="space-y-3">
            <label className="block">
              <div className="overline text-eduhu-inkSoft mb-1.5">Your email address</div>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="w-full bg-eduhu-bg2 border border-eduhu-border px-4 py-3 text-sm focus:outline-none focus:border-eduhu-dark"
              />
            </label>
            {err && <p className="text-xs text-eduhu-error">{err}</p>}
            <button disabled={busy} className="eduhu-btn-primary w-full justify-center">
              {busy ? "Sending…" : "Get my 10% discount"}
            </button>
          </form>
          <p className="text-xs text-center text-eduhu-inkSoft">
            No spam. Unsubscribe any time. One use per account.
          </p>
        </div>
      )}
    </Modal>
  );
}
